import { format } from 'date-fns';
import type { PayslipWithLineItems, PayslipLineItem, PayslipDetailsResponse } from './types';

export interface ExportOptions {
  format: 'pdf' | 'csv' | 'excel' | 'json';
  includeLineItems: boolean;
  includeSensitiveData: boolean;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

export interface PayslipExportData {
  payslip: PayslipWithLineItems;
  exportOptions: ExportOptions;
  generatedAt: Date;
}

class PayslipExportService {
  /**
   * Export payslip to CSV format
   */
  async exportToCSV(payslips: PayslipWithLineItems[], options: ExportOptions): Promise<string> {
    const headers = [
      'Pay Period Start',
      'Pay Period End', 
      'Employee ID',
      'Total Hours',
      'Gross Pay',
      'Total Deductions',
      'Net Pay',
      'Generated Date'
    ];

    if (options.includeLineItems) {
      headers.push(
        'Line Item Type',
        'Line Item Description', 
        'Line Item Amount',
        'Line Item Category'
      );
    }

    const rows = [headers.join(',')];

    for (const payslip of payslips) {
      const baseRow = [
        format(new Date(payslip.start_date), 'yyyy-MM-dd'),
        format(new Date(payslip.end_date), 'yyyy-MM-dd'),
        payslip.profile_id,
        payslip.total_hours?.toString() || '0',
        options.includeSensitiveData ? (payslip.gross_pay?.toString() || '0') : '****',
        options.includeSensitiveData ? (payslip.calculated_totals.total_deductions?.toString() || '0') : '****',
        options.includeSensitiveData ? (payslip.net_pay?.toString() || '0') : '****',
        format(new Date(payslip.created_at), 'yyyy-MM-dd HH:mm:ss')
      ];

      if (options.includeLineItems && payslip.line_items?.length > 0) {
        for (const lineItem of payslip.line_items) {
          const lineItemRow = [
            ...baseRow,
            lineItem.calculation_method || 'earning',
            `"${lineItem.description}"`,
            options.includeSensitiveData ? lineItem.base_amount.toString() : '****',
            lineItem.category || 'other'
          ];
          rows.push(lineItemRow.join(','));
        }
      } else {
        if (options.includeLineItems) {
          baseRow.push('', '', '', '');
        }
        rows.push(baseRow.join(','));
      }
    }

    return rows.join('\n');
  }

  /**
   * Export payslip to PDF format (mock implementation)
   */
  async exportToPDF(payslips: PayslipWithLineItems[], options: ExportOptions): Promise<Blob> {
    // In a real implementation, this would generate a proper PDF
    // using libraries like jsPDF or Puppeteer
    
    const pdfContent = this.generatePDFContent(payslips, options);
    return new Blob([pdfContent], { type: 'application/pdf' });
  }

  /**
   * Generate PDF content as HTML (for mock implementation)
   */
  private generatePDFContent(payslips: PayslipWithLineItems[], options: ExportOptions): string {
    const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Payslip Export</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; }
        .payslip { page-break-after: always; margin-bottom: 40px; }
        .header { border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 20px; }
        .summary { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0; }
        .line-items { margin-top: 20px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f2f2f2; }
        .amount { text-align: right; font-family: monospace; }
        .sensitive { ${options.includeSensitiveData ? '' : 'filter: blur(4px);'} }
      </style>
    </head>
    <body>
      ${payslips.map(payslip => this.generatePayslipHTML(payslip, options)).join('')}
    </body>
    </html>
    `;
    
    return htmlContent;
  }

  /**
   * Generate HTML for individual payslip
   */
  private generatePayslipHTML(payslip: PayslipWithLineItems, options: ExportOptions): string {
    const formatCurrency = (amount: number) => 
      options.includeSensitiveData ? `$${amount.toLocaleString()}` : '****';

    return `
      <div class="payslip">
        <div class="header">
          <h1>Payslip</h1>
          <p><strong>Pay Period:</strong> ${format(new Date(payslip.start_date), 'MMM dd, yyyy')} - ${format(new Date(payslip.end_date), 'MMM dd, yyyy')}</p>
          <p><strong>Generated:</strong> ${format(new Date(payslip.created_at), 'MMM dd, yyyy')}</p>
        </div>

        <div class="summary">
          <div>
            <h3>Pay Summary</h3>
            <p><strong>Total Hours:</strong> ${payslip.total_hours || 0}</p>
            <p><strong>Gross Pay:</strong> <span class="sensitive">${formatCurrency(payslip.gross_pay || 0)}</span></p>
            <p><strong>Total Deductions:</strong> <span class="sensitive">${formatCurrency(payslip.calculated_totals.total_deductions || 0)}</span></p>
            <p><strong>Net Pay:</strong> <span class="sensitive">${formatCurrency(payslip.net_pay || 0)}</span></p>
          </div>
          
          <div>
            <h3>Year-to-Date</h3>
            <p><strong>YTD Gross:</strong> <span class="sensitive">${formatCurrency(payslip.calculated_totals.ytd_totals.gross_earnings || 0)}</span></p>
            <p><strong>YTD Deductions:</strong> <span class="sensitive">${formatCurrency(payslip.calculated_totals.ytd_totals.total_deductions || 0)}</span></p>
            <p><strong>YTD Net:</strong> <span class="sensitive">${formatCurrency(payslip.calculated_totals.ytd_totals.net_pay || 0)}</span></p>
          </div>
        </div>

        ${options.includeLineItems && payslip.line_items?.length > 0 ? `
          <div class="line-items">
            <h3>Line Items Breakdown</h3>
            <table>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Category</th>
                  <th class="amount">Amount</th>
                </tr>
              </thead>
              <tbody>
                ${payslip.line_items.map(item => `
                  <tr>
                    <td>${item.description}</td>
                    <td>${item.calculation_method || 'earning'}</td>
                    <td>${item.category || 'Other'}</td>
                    <td class="amount sensitive">${formatCurrency(item.base_amount)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        ` : ''}
      </div>
    `;
  }

  /**
   * Export multiple payslips with specified options
   */
  async exportPayslips(payslips: PayslipWithLineItems[], options: ExportOptions): Promise<Blob> {
    switch (options.format) {
      case 'csv':
        const csvContent = await this.exportToCSV(payslips, options);
        return new Blob([csvContent], { type: 'text/csv' });
      
      case 'pdf':
        return await this.exportToPDF(payslips, options);
        
      case 'excel':
        // Mock Excel export - in real implementation would use libraries like ExcelJS
        const excelContent = await this.exportToCSV(payslips, options);
        return new Blob([excelContent], { type: 'application/vnd.ms-excel' });
        
      default:
        throw new Error(`Unsupported export format: ${options.format}`);
    }
  }

  /**
   * Generate export filename
   */
  generateFilename(exportFormat: string, dateRange?: { start: Date; end: Date }): string {
    const timestamp = format(new Date(), 'yyyy-MM-dd');
    const extension = exportFormat === 'excel' ? 'xlsx' : exportFormat;
    
    if (dateRange) {
      const startDate = format(dateRange.start, 'yyyy-MM-dd');
      const endDate = format(dateRange.end, 'yyyy-MM-dd');
      return `payslips_${startDate}_to_${endDate}.${extension}`;
    }
    
    return `payslips_export_${timestamp}.${extension}`;
  }

  /**
   * Download exported payslips
   */
  async downloadPayslips(payslips: PayslipWithLineItems[], options: ExportOptions): Promise<void> {
    try {
      const blob = await this.exportPayslips(payslips, options);
      const filename = this.generateFilename(options.format, options.dateRange);
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      
      URL.revokeObjectURL(url);
    } catch (error) {
      throw new Error(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }
}

export const payslipExportService = new PayslipExportService();

/**
 * Helper to trigger a file download from a Blob
 */
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Export a single payslip detail response as a CSV file and trigger download.
 */
export function exportPayslipDetailToCSV(
  detail: PayslipDetailsResponse,
  lineItems?: PayslipLineItem[]
): void {
  const { payslip, year_to_date_summary } = detail;
  const ct = payslip.calculated_totals;
  const items = lineItems ?? (payslip as PayslipWithLineItems).line_items ?? [];

  const rows: string[] = [];
  const esc = (v: string | number | null | undefined) => {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"`
      : s;
  };

  // Summary section
  rows.push('Section,Field,Value');
  rows.push(`Summary,Pay Period Start,${format(new Date(payslip.start_date), 'yyyy-MM-dd')}`);
  rows.push(`Summary,Pay Period End,${format(new Date(payslip.end_date), 'yyyy-MM-dd')}`);
  rows.push(`Summary,Issue Date,${format(new Date(payslip.created_at), 'yyyy-MM-dd')}`);
  rows.push(`Summary,Total Hours,${payslip.total_hours ?? 0}`);
  rows.push(`Summary,Gross Pay,${ct?.gross_earnings ?? payslip.gross_pay ?? 0}`);
  rows.push(`Summary,Total Deductions,${ct?.total_deductions ?? 0}`);
  rows.push(`Summary,Net Pay,${ct?.net_pay ?? payslip.net_pay ?? 0}`);

  // Tax breakdown
  if (ct?.tax_breakdown) {
    for (const [taxType, amount] of Object.entries(ct.tax_breakdown)) {
      rows.push(`Tax,${esc(taxType.replace(/_/g, ' '))},${amount}`);
    }
  }

  // YTD
  if (year_to_date_summary && year_to_date_summary.periods_count > 0) {
    rows.push(`YTD,Gross,${year_to_date_summary.total_gross}`);
    rows.push(`YTD,Net,${year_to_date_summary.total_net}`);
    rows.push(`YTD,Hours,${year_to_date_summary.total_hours}`);
    rows.push(`YTD,Periods,${year_to_date_summary.periods_count}`);
  }

  // Line items
  if (items.length > 0) {
    rows.push('');
    rows.push('Line Item Description,Type,Category,Amount');
    for (const li of items) {
      rows.push(
        [
          esc(li.description),
          esc(li.calculation_method ?? 'earning'),
          esc(li.category ?? 'other'),
          li.base_amount,
        ].join(',')
      );
    }
  }

  const filename = `payslip_${format(new Date(payslip.start_date), 'yyyy-MM-dd')}.csv`;
  downloadBlob(new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' }), filename);
}

/**
 * Export a single payslip detail response as a JSON file and trigger download.
 */
export function exportPayslipDetailToJSON(
  detail: PayslipDetailsResponse,
  lineItems?: PayslipLineItem[]
): void {
  const { payslip, previous_payslip, year_to_date_summary } = detail;
  const items = lineItems ?? (payslip as PayslipWithLineItems).line_items ?? [];

  const payload = {
    payslip: {
      id: payslip.id,
      start_date: payslip.start_date,
      end_date: payslip.end_date,
      created_at: payslip.created_at,
      total_hours: payslip.total_hours,
      gross_pay: payslip.gross_pay,
      net_pay: payslip.net_pay,
      calculated_totals: payslip.calculated_totals ?? null,
    },
    line_items: items.map((li) => ({
      description: li.description,
      calculation_method: li.calculation_method,
      category: li.category,
      base_amount: li.base_amount,
    })),
    previous_payslip: previous_payslip
      ? {
          id: previous_payslip.id,
          start_date: previous_payslip.start_date,
          end_date: previous_payslip.end_date,
          gross_pay: previous_payslip.gross_pay,
          net_pay: previous_payslip.net_pay,
        }
      : null,
    year_to_date_summary,
    exported_at: new Date().toISOString(),
  };

  const filename = `payslip_${format(new Date(payslip.start_date), 'yyyy-MM-dd')}.json`;
  downloadBlob(
    new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }),
    filename
  );
}